import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { VaultDocTypeCreateRequest, type VaultDocSummary } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const vaultRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vault',
  component: VaultPage,
});

type UploadFormValues = {
  typeId: string;
  title: string;
  identifierNo?: string;
  issueDate?: string;
  expiryDate?: string;
};

type DocTypeFormValues = {
  name: string;
  defaultReminderDaysCsv: string;
  defaultVisibility: 'admin_finance' | 'all_staff';
};

type ExpiryWindow = '' | '30' | '60' | '90';

function VaultPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [typeOpen, setTypeOpen] = React.useState(false);
  const [bundleMode, setBundleMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<Record<string, boolean>>({});
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [supersedeTarget, setSupersedeTarget] = React.useState<VaultDocSummary | null>(null);
  const [supersedeFile, setSupersedeFile] = React.useState<File | null>(null);
  const [typeFilter, setTypeFilter] = React.useState('');
  const [expiryWindow, setExpiryWindow] = React.useState<ExpiryWindow>('');

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const isAdmin = (meQuery.data?.person.roles ?? []).includes('Admin');

  const typesQuery = useQuery({
    queryKey: ['vault', 'doc-types'],
    queryFn: () => api.listVaultDocTypes(),
  });
  const docsQuery = useQuery({
    queryKey: ['vault', 'docs', typeFilter, expiryWindow],
    queryFn: () =>
      api.listVaultDocs({
        typeId: typeFilter || undefined,
        expiringWithinDays: expiryWindow ? Number(expiryWindow) : undefined,
      }),
  });

  const invalidateDocs = () => queryClient.invalidateQueries({ queryKey: ['vault', 'docs'] });

  const uploadForm = useForm<UploadFormValues>({
    defaultValues: { typeId: '', title: '' },
  });

  const uploadMutation = useMutation({
    mutationFn: async (values: UploadFormValues) => {
      if (!file) throw new Error('File required');
      const metadata = {
        typeId: values.typeId,
        title: values.title,
        identifierNo: values.identifierNo || undefined,
        issueDate: values.issueDate || undefined,
        expiryDate: values.expiryDate || undefined,
      };
      return api.uploadVaultDoc(metadata, file);
    },
    onSuccess: async () => {
      setUploadOpen(false);
      uploadForm.reset();
      setFile(null);
      setErrorMessage(null);
      await invalidateDocs();
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const supersedeForm = useForm<UploadFormValues>({
    defaultValues: { typeId: '', title: '' },
  });
  const openSupersede = (doc: VaultDocSummary) => {
    setSupersedeTarget(doc);
    setSupersedeFile(null);
    setErrorMessage(null);
    supersedeForm.reset({
      typeId: doc.typeId,
      title: doc.title,
      identifierNo: doc.identifierNo ?? '',
      issueDate: '',
      expiryDate: '',
    });
  };
  const supersedeMutation = useMutation({
    mutationFn: async (values: UploadFormValues) => {
      if (!supersedeTarget) throw new Error('No document selected');
      if (!supersedeFile) throw new Error('File required');
      return api.supersedeVaultDoc(
        supersedeTarget.id,
        {
          typeId: values.typeId,
          title: values.title,
          identifierNo: values.identifierNo || undefined,
          issueDate: values.issueDate || undefined,
          expiryDate: values.expiryDate || undefined,
        },
        supersedeFile,
      );
    },
    onSuccess: async () => {
      setSupersedeTarget(null);
      setSupersedeFile(null);
      setErrorMessage(null);
      await invalidateDocs();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const typeForm = useForm<DocTypeFormValues>({
    defaultValues: { name: '', defaultReminderDaysCsv: '60,30,7', defaultVisibility: 'admin_finance' },
  });
  const createTypeMutation = useMutation({
    mutationFn: async (values: DocTypeFormValues) => {
      const parsedReminderDays = values.defaultReminderDaysCsv
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      const body = VaultDocTypeCreateRequest.parse({
        name: values.name,
        defaultReminderDays: parsedReminderDays,
        defaultVisibility: values.defaultVisibility,
      });
      return api.createVaultDocType(body);
    },
    onSuccess: async () => {
      setTypeOpen(false);
      typeForm.reset();
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['vault', 'doc-types'] });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });
  const deactivateTypeMutation = useMutation({
    mutationFn: (id: string) => api.deactivateVaultDocType(id),
    onSuccess: async () => {
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['vault', 'doc-types'] });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const downloadDoc = async (doc: VaultDocSummary) => {
    try {
      const blob = await api.downloadVaultDoc(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = blob.type.split('/')[1]?.replace('jpeg', 'jpg') ?? 'bin';
      a.download = `${doc.title}-v${doc.version}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setErrorMessage(null);
    } catch (err) {
      setErrorMessage(toMessage(err));
    }
  };

  const bundleMutation = useMutation({
    mutationFn: async () => {
      const docIds = Object.entries(selectedIds).filter(([, v]) => v).map(([k]) => k);
      if (docIds.length === 0) throw new Error('Select at least one doc');
      const blob = await api.downloadShareBundle({ docIds });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bits-vault-bundle.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const docs = docsQuery.data ?? [];
  const types = typesQuery.data ?? [];
  const supersededIds = React.useMemo(
    () => new Set(docs.map((d) => d.supersedesId).filter((v): v is string => v !== null)),
    [docs],
  );
  const grouped = new Map<string, typeof docs>();
  for (const d of docs) {
    const arr = grouped.get(d.typeName) ?? [];
    arr.push(d);
    grouped.set(d.typeName, arr);
  }

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">Vault</h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Statutory docs with expiry-driven reminders. Superseded versions stay for audit.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:shrink-0">
          <Button variant={bundleMode ? 'default' : 'outline'} onClick={() => setBundleMode((b) => !b)}>
            {bundleMode ? 'Cancel bundle' : 'Bundle mode'}
          </Button>
          {bundleMode ? (
            <Button
              variant="default"
              disabled={bundleMutation.isPending || Object.values(selectedIds).every((v) => !v)}
              onClick={() => bundleMutation.mutate()}
            >
              {bundleMutation.isPending ? 'Preparing...' : 'Download bundle'}
            </Button>
          ) : null}
          <Dialog open={typeOpen} onOpenChange={setTypeOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">New doc type</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>New doc type</DialogTitle>
                <DialogDescription>
                  Reminders fire at each offset (days before expiry).
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={typeForm.handleSubmit((v) => createTypeMutation.mutate(v))}
                className="flex flex-col gap-3"
              >
                <Field label="Name" required error={typeForm.formState.errors.name?.message}>
                  <Input {...typeForm.register('name', { required: 'Enter a name' })} />
                </Field>
                <Field label="Reminder days (comma-separated)">
                  <Input {...typeForm.register('defaultReminderDaysCsv')} placeholder="60,30,7" />
                </Field>
                <Field label="Default visibility">
                  <Select {...typeForm.register('defaultVisibility')}>
                    <option value="admin_finance">Admin + Finance only</option>
                    <option value="all_staff">All staff</option>
                  </Select>
                </Field>
                <DialogFooter>
                  <Button type="submit" disabled={createTypeMutation.isPending}>
                    Create type
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button>Upload</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload doc</DialogTitle>
                <DialogDescription>Pick a doc type, then attach the file.</DialogDescription>
              </DialogHeader>
              <form
                onSubmit={uploadForm.handleSubmit((v) => uploadMutation.mutate(v))}
                className="flex flex-col gap-3"
              >
                <Field label="Type" required error={uploadForm.formState.errors.typeId?.message}>
                  <Select {...uploadForm.register('typeId', { required: 'Pick a doc type' })}>
                    <option value="">Select…</option>
                    {types
                      .filter((t) => t.active)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </Select>
                </Field>
                <Field label="Title" required error={uploadForm.formState.errors.title?.message}>
                  <Input {...uploadForm.register('title', { required: 'Enter a title' })} />
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Identifier no.">
                    <Input {...uploadForm.register('identifierNo')} />
                  </Field>
                  <Field label="Issue date">
                    <Input type="date" {...uploadForm.register('issueDate')} />
                  </Field>
                  <Field label="Expiry date">
                    <Input type="date" {...uploadForm.register('expiryDate')} />
                  </Field>
                  <Field label="File" required>
                    <Input
                      type="file"
                      onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
                    />
                  </Field>
                </div>
                {errorMessage ? (
                  <Alert variant="destructive">
                    <AlertTitle>Upload failed</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                ) : null}
                <DialogFooter>
                  <Button type="submit" disabled={uploadMutation.isPending || !file}>
                    {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {errorMessage && !uploadOpen && !supersedeTarget ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex w-full flex-col gap-1.5 sm:w-56">
          <Label>Type</Label>
          <Select value={typeFilter} onChange={(e) => setTypeFilter(e.currentTarget.value)}>
            <option value="">All types</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex w-full flex-col gap-1.5 sm:w-56">
          <Label>Expiry</Label>
          <Select
            value={expiryWindow}
            onChange={(e) => setExpiryWindow(e.currentTarget.value as ExpiryWindow)}
          >
            <option value="">Any</option>
            <option value="30">Expiring within 30 days</option>
            <option value="60">Expiring within 60 days</option>
            <option value="90">Expiring within 90 days</option>
          </Select>
        </div>
      </div>

      {docs.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center text-sm text-[hsl(var(--muted-foreground))]">
            {typeFilter || expiryWindow ? 'No docs match these filters.' : 'No docs yet.'}
          </CardContent>
        </Card>
      ) : (
        Array.from(grouped.entries()).map(([typeName, list]) => (
          <Card key={typeName}>
            <CardHeader>
              <CardTitle className="text-lg">{typeName}</CardTitle>
              <CardDescription>{list.length} doc{list.length === 1 ? '' : 's'}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
                {list.map((d) => {
                  const superseded = supersededIds.has(d.id);
                  const expiringSoon =
                    d.daysToExpiry !== null && d.daysToExpiry >= 0 && d.daysToExpiry <= 30;
                  return (
                    <li
                      id={d.id}
                      key={d.id}
                      className={
                        'flex flex-col gap-2 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3' +
                        (superseded ? ' opacity-60' : '')
                      }
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        {bundleMode ? (
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selectedIds[d.id] ?? false}
                            onChange={(e) =>
                              setSelectedIds((s) => ({ ...s, [d.id]: e.currentTarget.checked }))
                            }
                          />
                        ) : null}
                        <div>
                          <div className="font-medium">
                            {d.title}{' '}
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">v{d.version}</span>
                          </div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">
                            {d.identifierNo ? <>ID {d.identifierNo} · </> : null}
                            Issued {d.issueDate ?? '—'} · Expiry {d.expiryDate ?? '—'}
                          </div>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {superseded ? (
                          <Badge variant="muted">Superseded</Badge>
                        ) : expiringSoon ? (
                          <Badge variant="default">Expires in {d.daysToExpiry}d</Badge>
                        ) : d.daysToExpiry !== null && d.daysToExpiry < 0 ? (
                          <Badge variant="secondary">Expired</Badge>
                        ) : null}
                        {!bundleMode ? (
                          <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)}>
                            Download
                          </Button>
                        ) : null}
                        {isAdmin && !superseded && !bundleMode ? (
                          <Button variant="outline" size="sm" onClick={() => openSupersede(d)}>
                            Upload new version
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        ))
      )}

      {isAdmin ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Doc types</CardTitle>
            <CardDescription>
              Deactivated types keep their documents but cannot receive new uploads.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {types.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">No doc types yet.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
                {types.map((t) => (
                  <li key={t.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium">{t.name}</span>
                      <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">
                        reminders {t.defaultReminderDays.join('/')}d ·{' '}
                        {t.defaultVisibility === 'all_staff' ? 'all staff' : 'Admin + Finance'}
                      </span>
                    </div>
                    {t.active ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={deactivateTypeMutation.isPending}
                        onClick={() => deactivateTypeMutation.mutate(t.id)}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Badge variant="muted">Inactive</Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={supersedeTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSupersedeTarget(null);
            setErrorMessage(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload new version</DialogTitle>
            <DialogDescription>
              {supersedeTarget
                ? `Supersedes “${supersedeTarget.title}” v${supersedeTarget.version}. The old version stays on record.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={supersedeForm.handleSubmit((v) => supersedeMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <Field label="Type" required error={supersedeForm.formState.errors.typeId?.message}>
              <Select {...supersedeForm.register('typeId', { required: 'Pick a doc type' })}>
                <option value="">Select…</option>
                {types
                  .filter((t) => t.active || t.id === supersedeTarget?.typeId)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="Title" required error={supersedeForm.formState.errors.title?.message}>
              <Input {...supersedeForm.register('title', { required: 'Enter a title' })} />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Identifier no.">
                <Input {...supersedeForm.register('identifierNo')} />
              </Field>
              <Field label="Issue date">
                <Input type="date" {...supersedeForm.register('issueDate')} />
              </Field>
              <Field label="Expiry date">
                <Input type="date" {...supersedeForm.register('expiryDate')} />
              </Field>
              <Field label="File" required>
                <Input
                  type="file"
                  onChange={(e) => setSupersedeFile(e.currentTarget.files?.[0] ?? null)}
                />
              </Field>
            </div>
            {errorMessage ? (
              <Alert variant="destructive">
                <AlertTitle>Upload failed</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button type="submit" disabled={supersedeMutation.isPending || !supersedeFile}>
                {supersedeMutation.isPending ? 'Uploading...' : 'Upload new version'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
